import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Ban, RotateCcw, Download, Radio, ChevronDown, ChevronRight,
  CheckSquare, Square, Check, AlertTriangle, Circle, PencilLine, ArrowRight, Plus,
} from 'lucide-react';
import { useRunEvents } from '../../hooks/useRunEvents';
import pipelineService, { PipelineRun, ExtractedField } from '../../services/pipelineService';
import docshawkService, { ApprovedField, CandidateField } from '../../services/docshawkService';
import { StatusPill, PageHeader, Spinner, Button } from '../../components/ui';
import Stepper from '../../components/ui/Stepper';
import EventTimeline from '../../components/ui/EventTimeline';
import DocViewer from '../../components/DocViewer';
import AgentChatPanel from '../../components/AgentChatPanel';
import '../../styles/theme.css';
import '../../styles/review-theme.css';
import '../../styles/review-workspace.css';

interface Sel { value: string; page?: number; }

// Reconcile all streams' fields into one best value per term (grounded wins, then confidence) —
// the same precedence the export uses.
function reconcile(fields: ExtractedField[]): Record<string, ExtractedField> {
  const best: Record<string, ExtractedField> = {};
  const rank = (x: ExtractedField) => (x.grounded ? 10 : 0) + (x.confidence || 0);
  (fields || []).forEach((f) => {
    if (f.value == null || f.value === '') return;
    const cur = best[f.field_key];
    if (!cur || rank(f) > rank(cur)) best[f.field_key] = f;
  });
  return best;
}

const RunDetail: React.FC = () => {
  const { runId } = useParams<{ runId: string }>();
  const navigate = useNavigate();
  const { run, feed, connected, error, reload } = useRunEvents(runId);
  const [busy, setBusy] = useState('');
  const [sel, setSel] = useState<Sel | null>(null);
  const [showLog, setShowLog] = useState(false);
  const [schema, setSchema] = useState<CandidateField[]>([]);

  useEffect(() => {
    docshawkService.getActiveSchema().then((r) => setSchema(r.current_version?.fields || [])).catch(() => {});
  }, []);

  const gate = run?.hitl_gate?.gate;
  const activeRun = run && ['running', 'paused', 'cancelling'].includes(run.status);
  const terminal = run && ['succeeded', 'failed', 'cancelled'].includes(run.status);
  const act = async (label: string, fn: () => Promise<any>) => {
    setBusy(label);
    try { await fn(); await reload(); } finally { setBusy(''); }
  };

  if (!run) {
    return <div className="dh-review"><PageHeader title="Document" /><div className="mx-card mx-card-pad">
      {error ? <span className="mx-muted">{error}</span> : <Spinner label="Loading…" />}
    </div></div>;
  }

  const isPdf = (run.document_filename || '').toLowerCase().endsWith('.pdf');
  const controls = (
    <>
      {connected && <span className="mx-chip" style={{ color: 'var(--mx-success)' }}>
        <Radio size={12} className="mx-pulsing" /> live</span>}
      {activeRun && <Button icon={Ban} variant="danger" size="sm" disabled={!!busy}
        onClick={() => act('cancel', () => pipelineService.cancelRun(run.id))}>Cancel</Button>}
      {terminal && <Button icon={RotateCcw} variant="secondary" size="sm" disabled={!!busy}
        onClick={() => act('rerun', () => pipelineService.rerunFromStage(run.id))}>Re-read</Button>}
      {run.export_uri && <Button icon={Download} variant="secondary" size="sm"
        onClick={() => downloadCsv(run.id)}>CSV</Button>}
    </>
  );

  return (
    <div className="dh-review">
      <Button icon={ArrowLeft} variant="ghost" size="sm" style={{ marginBottom: 12, paddingLeft: 4 }}
        onClick={() => navigate('/admin/docshawk')}>Documents</Button>
      <PageHeader title={run.document_filename || 'Document'}
        subtitle={run.status === 'succeeded' ? 'Review complete'
          : gate === 'sanity_check' ? 'Ready for your review'
          : gate === 'schema_approval' ? 'Confirm the terms to track'
          : 'Reading your document…'}
        actions={<div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <StatusPill status={run.status} pulse /> {controls}</div>} />

      <div className="mx-card mx-card-pad" style={{ marginBottom: 16 }}>
        <Stepper stages={run.stages} />
      </div>

      <div className="mx-grid" style={{ gridTemplateColumns: '1.4fr 1fr', alignItems: 'start' }}>
        <DocViewer documentId={run.document_id} mimeType={isPdf ? 'application/pdf' : 'text/plain'}
          highlight={sel?.value} highlightPage={sel?.page} />

        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {gate === 'schema_approval' && <SchemaGate run={run} onDone={reload} />}
          {run.status === 'succeeded' && (
            <div className="dh-panel" style={{ padding: '12px 15px', display: 'flex', alignItems: 'center', gap: 10 }}>
              <Check size={18} style={{ color: 'var(--dh-verified)', flex: 'none' }} />
              <div style={{ fontSize: 13, color: 'var(--dh-ink-soft)' }}>
                <b style={{ color: 'var(--dh-ink)' }}>Review complete.</b> Every value carries its page and passage.</div>
              <Button icon={ArrowRight} tone="paper" variant="secondary" style={{ marginLeft: 'auto', border: '1px solid var(--dh-line-strong)' }}
                onClick={() => navigate(`/admin/docshawk/runs/${run.id}/results`)}>View results</Button>
              <Button icon={Download} tone="paper" variant="primary" onClick={() => downloadCsv(run.id)}>CSV</Button>
            </div>
          )}
          <TermReview run={run} schema={schema} selValue={sel?.value} onSelect={setSel} onDone={reload} />

          <AgentChatPanel agentKey="doc" runId={runId} />

          <div className="mx-card">
            <button className="mx-card-head" style={{ width: '100%', background: 'none', border: 'none', cursor: 'pointer' }}
              onClick={() => setShowLog((v) => !v)}>
              <h3 className="mx-card-title">Processing details</h3>
              {showLog ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
            </button>
            {showLog && <div className="mx-card-pad" style={{ maxHeight: 320, overflowY: 'auto' }}>
              <EventTimeline feed={feed} live={!!connected} /></div>}
          </div>
        </div>
      </div>
    </div>
  );
};

// ------------------------------------------------------------------ reconciled term list
const TermReview: React.FC<{
  run: PipelineRun; schema: CandidateField[]; selValue?: string;
  onSelect: (s: Sel) => void; onDone: () => void;
}> = ({ run, schema, selValue, onSelect, onDone }) => {
  const atGate = run.hitl_gate?.gate === 'sanity_check' && run.status === 'paused';
  const gateFields: ExtractedField[] = run.hitl_gate?.payload?.fields || [];
  const baseFields = atGate && gateFields.length ? gateFields : (run.extracted_fields || []);
  const best = useMemo(() => reconcile(baseFields), [baseFields]);

  const labelOf = (k: string) => schema.find((s) => s.key === k)?.label || k;

  const keys = useMemo(() => {
    const s = new Set<string>();
    schema.forEach((f) => f.key && s.add(f.key));
    Object.keys(best).forEach((k) => s.add(k));
    return Array.from(s);
  }, [schema, best]);

  const [edits, setEdits] = useState<Record<string, string>>({});
  const [correcting, setCorrecting] = useState<Record<string, boolean>>({});
  const [confirmed, setConfirmed] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const [tests, setTests] = useState<any>(run.hitl_gate?.payload?.tests || null);
  const [blocked, setBlocked] = useState<number>(run.hitl_gate?.payload?.tests?.required_failures || 0);

  const resolvedOf = (k: string) => confirmed.has(k)
    || (edits[k] != null && edits[k] !== (best[k]?.value ?? ''));

  const { needs, verified, notfound } = useMemo(() => {
    const n: string[] = [], v: string[] = [], nf: string[] = [];
    keys.forEach((k) => {
      const f = best[k];
      if (!f || f.value == null || f.value === '') nf.push(k);
      else if (f.grounded) v.push(k);
      else n.push(k);
    });
    return { needs: n, verified: v, notfound: nf };
  }, [keys, best]);

  const click = (k: string) => { const f = best[k]; if (f) onSelect({ value: f.value, page: f.provenance?.page }); };

  const submit = async (override = false) => {
    setBusy(true);
    const corrections = Object.entries(edits)
      .filter(([k, v]) => v !== (best[k]?.value ?? ''))
      .map(([k, v]) => ({
        field_key: k, corrected_value: v,
        source_snippet: best[k]?.provenance?.source_snippet || undefined, page: best[k]?.provenance?.page || 0,
      }));
    try {
      const res = await pipelineService.resumeRun(run.id, corrections, override);
      if (res.blocked) { setTests(res.tests); setBlocked(res.tests.required_failures); }
      else onDone();
    } finally { setBusy(false); }
  };

  const cite = (f?: ExtractedField) => (f?.provenance?.page ? `p. ${f.provenance.page}` : '');

  return (
    <div className="dh-panel">
      <div className="dh-phead">
        <span className="dh-ptitle">Extracted terms</span>
        <span className="dh-cite">{verified.length} of {keys.length} found</span>
      </div>

      {atGate && tests?.results?.length ? (
        <div className="dh-tests">
          {tests.results.filter((t: any) => t.passed === false).slice(0, 4).map((t: any) => (
            <div key={t.key} className="dh-test">
              <AlertTriangle size={13} style={{ color: t.required ? 'var(--dh-review)' : 'var(--dh-ink-faint)' }} />
              <span style={{ fontWeight: t.required ? 600 : 400 }}>{t.name}</span>
              {t.required && <span className="dh-pill rev">must fix</span>}
              <span className="dt">{t.detail}</span>
            </div>
          ))}
        </div>
      ) : null}

      <div className="dh-body">
        {/* NEEDS REVIEW */}
        {needs.length > 0 && (
          <div className="dh-grp review">
            <div className="dh-grp-t"><AlertTriangle size={13} /> Needs your review <span className="dh-c">{needs.length}</span></div>
            {needs.map((k) => {
              const f = best[k]; const done = resolvedOf(k);
              return (
                <div key={k} className={`dh-term ${selValue === f.value ? 'sel' : ''} ${done ? 'resolved' : ''}`}
                  onClick={() => click(k)}>
                  <div className="dh-lbl">
                    {done ? <Check size={13} style={{ color: 'var(--dh-verified)' }} /> : <AlertTriangle size={13} style={{ color: 'var(--dh-review)' }} />}
                    {labelOf(k)}{done && <span className="dh-pill ok" style={{ marginLeft: 'auto' }}>{edits[k] != null ? 'corrected' : 'confirmed'}</span>}
                  </div>
                  <div className="dh-val">{edits[k] ?? f.value}</div>
                  {!done && (
                    <div className="dh-flag">
                      <AlertTriangle size={14} />
                      <div>We couldn’t verify this wording in the document.
                        {f.provenance?.source_snippet && <> Closest passage — <span className="dh-cite">{cite(f)}</span>:{' '}
                          <span style={{ fontStyle: 'italic' }}>“{f.provenance.source_snippet.slice(0, 90)}”</span></>}</div>
                    </div>
                  )}
                  {atGate && !done && (correcting[k]
                    ? <input className="dh-input" autoFocus defaultValue={f.value}
                        onClick={(e) => e.stopPropagation()}
                        onKeyDown={(e) => { if (e.key === 'Enter') { setEdits((s) => ({ ...s, [k]: (e.target as HTMLInputElement).value })); setCorrecting((c) => ({ ...c, [k]: false })); } }}
                        onBlur={(e) => { setEdits((s) => ({ ...s, [k]: e.target.value })); setCorrecting((c) => ({ ...c, [k]: false })); }} />
                    : (
                      <div className="dh-acts" onClick={(e) => e.stopPropagation()}>
                        <button className="dh-mini ok" onClick={() => setConfirmed((s) => new Set(s).add(k))}><Check size={13} /> Confirm</button>
                        <button className="dh-mini" onClick={() => setCorrecting((c) => ({ ...c, [k]: true }))}><PencilLine size={13} /> Correct…</button>
                        <span className="dh-cite" style={{ cursor: 'pointer' }} onClick={() => click(k)}>Show in document →</span>
                      </div>
                    ))}
                </div>
              );
            })}
          </div>
        )}

        {/* VERIFIED */}
        {verified.length > 0 && (
          <div className="dh-grp">
            <div className="dh-grp-t"><Check size={13} style={{ color: 'var(--dh-verified)' }} /> Verified in document <span className="dh-c">{verified.length}</span></div>
            {verified.map((k) => {
              const f = best[k];
              return (
                <div key={k} className={`dh-row ${selValue === f.value ? 'sel' : ''}`} onClick={() => click(k)}>
                  <Check size={15} style={{ color: 'var(--dh-verified)', flex: 'none' }} />
                  <span className="dh-rname">{labelOf(k)}</span>
                  <span className="dh-rval" title={f.value}>{f.value}</span>
                  <span className="dh-rp">{cite(f)}</span>
                </div>
              );
            })}
          </div>
        )}

        {/* NOT FOUND */}
        {notfound.length > 0 && (
          <div className="dh-grp">
            <div className="dh-grp-t">Not found in this agreement <span className="dh-c">{notfound.length}</span></div>
            {notfound.map((k) => (
              <div key={k} className="dh-row na">
                <Circle size={15} style={{ color: 'var(--dh-ink-faint)', flex: 'none' }} />
                <span className="dh-rname" style={{ color: 'var(--dh-ink-faint)', fontWeight: 500 }}>{labelOf(k)}</span>
                <span className="dh-cite">not present</span>
              </div>
            ))}
          </div>
        )}

        {keys.length === 0 && <div className="dh-cite" style={{ padding: '8px 2px' }}>No terms extracted yet…</div>}
      </div>

      {atGate && (
        <div className="dh-foot">
          <span className="sum"><b>{verified.length}</b> verified · <b style={{ color: 'var(--dh-review)' }}>{needs.length}</b> to review · <b>{notfound.length}</b> not found</span>
          <div style={{ display: 'flex', gap: 14, alignItems: 'center' }}>
            {blocked > 0 && <button className="dh-override" disabled={busy} onClick={() => submit(true)}>Finish anyway (noted in export)</button>}
            <Button icon={Check} tone="paper" variant="primary" disabled={busy} onClick={() => submit(false)}>
              {busy ? 'Finishing…' : 'Approve & finish'}</Button>
          </div>
        </div>
      )}
      {atGate && blocked > 0 && (
        <div style={{ padding: '0 15px 12px', color: 'var(--dh-review)', fontSize: 12 }}>
          {blocked} required check{blocked === 1 ? '' : 's'} unresolved — correct a value, or finish anyway.
        </div>
      )}
    </div>
  );
};

// ------------------------------------------------------------------ first-document schema gate (unchanged)
const SchemaGate: React.FC<{ run: PipelineRun; onDone: () => void }> = ({ run, onDone }) => {
  const cands: any[] = run.hitl_gate?.payload?.candidate_fields || [];
  const [rows, setRows] = useState(() => cands.map((c) => ({
    ...c, accepted: c.candidate_value != null, editedValue: c.candidate_value || '',
  })));
  const [busy, setBusy] = useState(false);
  const upd = (i: number, patch: any) => setRows((r) => r.map((x, j) => (j === i ? { ...x, ...patch } : x)));
  const submit = async () => {
    setBusy(true);
    const fields: ApprovedField[] = rows.filter((r) => r.accepted && r.key && r.key.trim()).map((r) => ({
      key: r.key, label: r.label || r.key, data_type: r.data_type || 'string', description: r.description,
      regex_hint: r.regex_hint, required: r.required, example: r.example,
      approved_value: r.editedValue, source_snippet: r.source_snippet, page: r.page || 0,
    }));
    try { await docshawkService.approveSchema(run.id, fields); onDone(); } finally { setBusy(false); }
  };
  return (
    <div className="mx-card" style={{ borderColor: 'var(--mx-warning)' }}>
      <div className="mx-card-head" style={{ background: 'var(--mx-warning-weak)' }}>
        <h3 className="mx-card-title">Set up your checklist</h3>
        <span className="mx-subtle" style={{ fontSize: 12 }}>{rows.filter((r) => r.accepted).length} selected</span>
      </div>
      <div className="mx-card-pad" style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 340, overflowY: 'auto' }}>
        <div className="mx-muted" style={{ fontSize: 12.5 }}>We found these terms in your first document. Confirm which ones to track from now on.</div>
        {rows.map((r, i) => (
          <div key={i} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', opacity: r.accepted ? 1 : 0.55 }}>
            <button style={{ background: 'none', border: 'none', cursor: 'pointer', marginTop: 2,
              color: r.accepted ? 'var(--mx-success)' : 'var(--mx-text-subtle)' }}
              onClick={() => upd(i, { accepted: !r.accepted })}>
              {r.accepted ? <CheckSquare size={18} /> : <Square size={18} />}</button>
            <div style={{ flex: 1, minWidth: 0 }}>
              {r.custom ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <input className="mx-input mx-mono" style={{ padding: '5px 8px', fontSize: 12 }}
                    placeholder="term key (e.g. administrative_agent)" value={r.key}
                    onChange={(e) => upd(i, { key: e.target.value })} />
                  <input className="mx-input" style={{ padding: '5px 8px', fontSize: 12 }}
                    placeholder="what to look for (plain description)" value={r.description || ''}
                    onChange={(e) => upd(i, { description: e.target.value })} />
                </div>
              ) : (
                <>
                  <div style={{ fontWeight: 600, fontSize: 13 }}>{r.label || r.key}
                    {r.required && <span style={{ color: 'var(--mx-danger)' }}> *</span>}</div>
                  <input className="mx-input" style={{ marginTop: 4, padding: '5px 8px', fontSize: 13 }}
                    value={r.editedValue} disabled={!r.accepted}
                    onChange={(e) => upd(i, { editedValue: e.target.value })} />
                </>
              )}
            </div>
          </div>
        ))}
        <Button icon={Plus} variant="ghost" size="sm" style={{ alignSelf: 'flex-start' }}
          onClick={() => setRows((r) => [...r, { key: '', label: '', data_type: 'string', regex_hint: '',
            description: '', required: false, accepted: true, editedValue: '', custom: true } as any])}>
          Add a term
        </Button>
      </div>
      <div className="mx-card-pad" style={{ borderTop: '1px solid var(--mx-border)' }}>
        <Button icon={Check} variant="primary" disabled={busy} onClick={submit}>
          {busy ? 'Saving…' : 'Confirm & continue'}</Button>
        <span className="mx-subtle" style={{ fontSize: 11, marginLeft: 10 }}>edit the full list later under Terms Checklist</span>
      </div>
    </div>
  );
};

async function downloadCsv(runId: string) {
  const text = await pipelineService.downloadExport(runId);
  const url = URL.createObjectURL(new Blob([text], { type: 'text/csv' }));
  const a = document.createElement('a');
  a.href = url; a.download = `docshawk_${runId}.csv`; a.click();
  URL.revokeObjectURL(url);
}

export default RunDetail;
